import { create } from 'zustand';
import type { PerRobotResult } from '../api/types';
import { t } from '../i18n';
import { robotName } from '../live/store';
import { dockErrorText } from '../lib/chargerState';

export type ToastKind = 'info' | 'ok' | 'err' | 'warn';
export interface ToastAction {
  label: string;
  onClick: () => void;
}
interface ToastItem {
  id: number;
  kind: ToastKind;
  text: string;
  action?: ToastAction;
}

interface ToastState {
  items: ToastItem[];
  push: (text: string, kind?: ToastKind, action?: ToastAction) => void;
  dismiss: (id: number) => void;
}

let seq = 1;
export const useToasts = create<ToastState>((set, get) => ({
  items: [],
  push: (text, kind = 'info', action) => {
    const id = seq++;
    set({ items: [...get().items.slice(-4), { id, kind, text, action }] });
    setTimeout(() => get().dismiss(id), action ? 12000 : kind === 'err' || kind === 'warn' ? 7000 : 3500);
  },
  dismiss: (id) => set({ items: get().items.filter((x) => x.id !== id) }),
}));

export const toast = (text: string, kind: ToastKind = 'info', action?: ToastAction) => useToasts.getState().push(text, kind, action);
export const toastErr = (e: unknown, prefix?: string) => {
  const msg = e instanceof Error ? e.message : String(e);
  toast(prefix ? `${prefix}: ${msg}` : msg, 'err');
};

export function perRobotToast(what: string, results: PerRobotResult[]) {
  const ok = results.filter((r) => r.ok).length;
  const bad = results.filter((r) => !r.ok);
  if (!bad.length) toast(t('toast.okOn', { what, n: ok }), 'ok');
  else if (bad.every((b) => dockErrorText(b.error)))
    toast(bad.map((b) => `${b.name ?? robotName(b.serial)}: ${dockErrorText(b.error)}`).join('; '), 'err');
  else if (bad.every((b) => b.error?.code === 'HAND_PUSH')) toast(`${bad.map((b) => b.name ?? robotName(b.serial)).join(', ')}: ${t('hand.blocked')}`, 'err');
  else
    toast(
      t('toast.partial', {
        what,
        ok,
        bad: bad.length,
        detail: bad.map((b) => `${b.name ?? robotName(b.serial)}: ${b.error?.code ?? ''} ${b.error?.message ?? ''}`.trim()).join('; '),
      }),
      'err',
    );
}

